import ApiBase from '@framework/services/ApiBase';

/**
 * RecipeApiService - Recipe Genie specific API methods.
 * Extends the shared ApiBase with food/recipe endpoints.
 */
class RecipeApiService extends ApiBase {
  // Recipes (with pagination via X-Total-Count header)
  async getRecipes(params = {}) {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
    ).toString();
    const url = `/food/recipes${query ? `?${query}` : ''}`;

    // Use fetch directly to access X-Total-Count header
    const headers = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    headers['Content-Type'] = 'application/json';

    const response = await fetch(`${this.baseURL}${url}`, { headers });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    const total = parseInt(response.headers.get('X-Total-Count') || '0', 10);
    return { recipes: data, total };
  }

  async getRecipe(recipeId) {
    return this.request(`/food/recipes/${recipeId}`);
  }

  async createRecipe(recipeData) {
    return this.post('/food/recipes', recipeData);
  }

  async updateRecipe(recipeId, recipeData) {
    return this.put(`/food/recipes/${recipeId}`, recipeData);
  }

  async deleteRecipe(recipeId) {
    return this.delete(`/food/recipes/${recipeId}`);
  }

  // Community recipes
  async getCommunityRecipes(params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/food/recipes/community${query ? `?${query}` : ''}`);
  }

  // Book import
  async importBook(file, pageStart, pageEnd) {
    const formData = new FormData();
    formData.append('file', file);
    if (pageStart) formData.append('page_start', pageStart);
    if (pageEnd) formData.append('page_end', pageEnd);

    // Use fetch directly — FormData needs browser to set Content-Type with boundary
    const headers = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

    const response = await fetch(`${this.baseURL}/food/import-book`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || `Upload failed: ${response.status}`);
    }
    return response.json();
  }

  // Image generation
  async generateImage(recipeId, provider = null) {
    const params = provider ? `?provider=${provider}` : '';
    return this.post(`/food/recipes/${recipeId}/generate-image${params}`);
  }

  async getImageStatus(taskId) {
    return this.request(`/food/recipe-image-status/${taskId}`);
  }

  async getJobStatus(jobId) {
    return this.request(`/tasks/${jobId}/status`);
  }

  // Book import history
  async getBookImports() {
    return this.request('/food/book-imports');
  }

  async getBookImportDetail(importId) {
    return this.request(`/food/book-imports/${importId}`);
  }

  async retryBookImport(importId) {
    return this.post(`/food/book-imports/${importId}/retry`);
  }

  async getRecipeSourceBook(recipeId) {
    return this.request(`/food/recipes/${recipeId}/source-book`);
  }

  // AI Improve Notes
  async improveNotes(recipeId, provider = null) {
    const params = provider ? `?provider=${provider}` : '';
    return this.post(`/food/recipes/${recipeId}/improve-notes${params}`);
  }

  async getImproveNotesStatus(taskId) {
    return this.request(`/food/improve-notes-status/${taskId}`);
  }

  // AI Generate Description
  async generateDescription(recipeId, provider = null) {
    const params = provider ? `?provider=${provider}` : '';
    return this.post(`/food/recipes/${recipeId}/generate-description${params}`);
  }

  async getGenerateDescriptionStatus(taskId) {
    return this.request(`/food/generate-description-status/${taskId}`);
  }

  // USDA Nutrition Calculator
  async calculateNutrition(recipeId) {
    return this.post(`/food/recipes/${recipeId}/calculate-nutrition`);
  }

  async getNutritionStatus(taskId) {
    return this.request(`/food/nutrition-status/${taskId}`);
  }

  // Tier limits (admin)
  async getTierLimits() {
    return this.request('/users/tier-limits');
  }

  async updateTierLimit(tier, updates) {
    return this.patch(`/users/tier-limits/${tier}`, updates);
  }

  // Encyclopedia browsing — type: 'ingredients'|'cuisines'|'dishes'|'techniques'|'equipment'
  async getEncyclopedia(type, params = {}) {
    const query = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null && v !== '')
    ).toString();
    return this.request(`/food/encyclopedia/${type}${query ? `?${query}` : ''}`);
  }

  // Entity detail — type: 'ingredient'|'cuisine'|'dish'|'technique'|'equipment'
  async getEntityInfo(type, name) {
    return this.request(`/food/${type}-info/${encodeURIComponent(name)}`);
  }

  // Entity info generation status polling
  async getEntityInfoStatus(type, taskId) {
    return this.request(`/food/${type}-info-status/${taskId}`);
  }

  // Help articles (admin) — Zenie chat help content
  async getHelpArticles() {
    return this.request('/food/help-articles');
  }

  async getHelpArticle(key) {
    return this.request(`/food/help-articles/${key}`);
  }

  async createHelpArticle(data) {
    return this.post('/food/help-articles', data);
  }

  async updateHelpArticle(key, updates) {
    return this.patch(`/food/help-articles/${key}`, updates);
  }

  async deleteHelpArticle(key) {
    return this.delete(`/food/help-articles/${key}`);
  }

  // Play Store keyword ranking tracker (admin)
  async getKeywords() {
    return this.request('/admin/keyword-rankings/keywords');
  }

  async addKeyword(keyword, platform = 'android') {
    return this.post('/admin/keyword-rankings/keywords', { keyword, platform });
  }

  async removeKeyword(id) {
    return this.delete(`/admin/keyword-rankings/keywords/${id}`);
  }

  async getRankings() {
    return this.request('/admin/keyword-rankings');
  }

  async startScan(keywordIds = null) {
    return this.post('/admin/keyword-rankings/scan', { keyword_ids: keywordIds });
  }

  async getScanStatus(jobId) {
    return this.request(`/admin/keyword-rankings/scan/${jobId}`);
  }

  // Recipe sites (admin)
  async getRecipeSites() {
    return this.request('/recipe-sites/admin');
  }

  async createRecipeSite(data) {
    return this.post('/recipe-sites/admin', data);
  }

  async updateRecipeSite(id, updates) {
    return this.patch(`/recipe-sites/admin/${id}`, updates);
  }

  async deleteRecipeSite(id) {
    return this.delete(`/recipe-sites/admin/${id}`);
  }

  async reorderRecipeSites(items) {
    return this.post('/recipe-sites/admin/reorder', items);
  }

  // Sitemap import jobs (admin)
  async getSitemapJobs() {
    return this.request('/admin/sitemap-jobs');
  }

  async createSitemapJob(data) {
    return this.post('/admin/sitemap-jobs', data);
  }

  async cancelSitemapJob(id) {
    return this.delete(`/admin/sitemap-jobs/${id}`);
  }
}

export default function createRecipeApi(baseURL) {
  return new RecipeApiService(baseURL);
}
